import QueryTags from '../types/QueryTags'
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions'
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react'

const basePath = 'membership/recurring-expenses'

export type RecurringExpenseOccurrencePayload = {
  occurredAt: string;   // ISO local date-time, e.g. 2026-10-08T14:30
  amount: number;
  currency?: string;
  notes?: string;
}

const recurringExpensesEndpoints = (builder: EndpointBuilder<
  BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>, QueryTags, 'data'>) =>
({
  // ----- expense definitions (one parent per expense) -----

  fetchRecurringExpenses: builder.query<any, { pageNumber?: number; pageSize?: number; sortBy?: string; sortDir?: 'asc' | 'desc' }>({
    query: ({ pageNumber = 0, pageSize = 10, sortBy, sortDir } = {}) => {
      const params = new URLSearchParams()
      params.set('pageNumber', String(pageNumber))
      params.set('pageSize', String(pageSize))
      if (sortBy) params.set('sortBy', sortBy)
      if (sortDir) params.set('sortDir', sortDir)
      return { url: `${basePath}?${params.toString()}`, method: 'get' }
    },
    providesTags: ['recurring-expenses'],
  }),

  fetchRecurringExpense: builder.query<any, number | string>({
    query: (id) => ({ url: `${basePath}/${id}`, method: 'get' }),
    providesTags: ['recurring-expenses'],
  }),

  fetchDueRecurringExpenses: builder.query<any[], void>({
    query: () => ({ url: `${basePath}/due`, method: 'get' }),
    providesTags: ['recurring-expenses'],
  }),

  fetchRecurringExpensesReport: builder.query<any, { startDate: string; endDate: string }>({
    query: ({ startDate, endDate }) => ({
      url: `${basePath}/report?startDate=${startDate}&endDate=${endDate}`,
      method: 'get',
    }),
    providesTags: ['recurring-expense-occurrences'],
  }),

  createRecurringExpense: builder.mutation<any, any>({
    query: (body) => ({ url: `${basePath}/create`, method: 'post', body }),
    invalidatesTags: ['recurring-expenses'],
  }),

  updateRecurringExpense: builder.mutation<any, { id: number; body: any }>({
    query: ({ id, body }) => ({ url: `${basePath}/${id}`, method: 'put', body }),
    invalidatesTags: ['recurring-expenses'],
  }),

  deleteRecurringExpense: builder.mutation<any, number>({
    query: (id) => ({ url: `${basePath}/${id}`, method: 'delete' }),
    invalidatesTags: ['recurring-expenses', 'recurring-expense-occurrences'],
  }),

  // ----- occurrences (each actual payment) -----

  fetchRecurringExpenseOccurrences: builder.query<any, { expenseId: number | string; pageNumber?: number; pageSize?: number }>({
    query: ({ expenseId, pageNumber = 0, pageSize = 10 }) => ({
      url: `${basePath}/${expenseId}/occurrences?pageNumber=${pageNumber}&pageSize=${pageSize}`,
      method: 'get',
    }),
    providesTags: ['recurring-expense-occurrences'],
  }),

  addRecurringExpenseOccurrence: builder.mutation<any, { expenseId: number | string; body: RecurringExpenseOccurrencePayload }>({
    query: ({ expenseId, body }) => ({ url: `${basePath}/${expenseId}/occurrences`, method: 'post', body }),
    invalidatesTags: ['recurring-expenses', 'recurring-expense-occurrences'],
  }),

  updateRecurringExpenseOccurrence: builder.mutation<any, { id: number; body: RecurringExpenseOccurrencePayload }>({
    query: ({ id, body }) => ({ url: `${basePath}/occurrences/${id}`, method: 'put', body }),
    invalidatesTags: ['recurring-expenses', 'recurring-expense-occurrences'],
  }),

  deleteRecurringExpenseOccurrence: builder.mutation<any, number>({
    query: (id) => ({ url: `${basePath}/occurrences/${id}`, method: 'delete' }),
    invalidatesTags: ['recurring-expenses', 'recurring-expense-occurrences'],
  }),

  reviewRecurringExpenseOccurrence: builder.mutation<any, { id: number; accountId: number; feeAmount?: number }>({
    query: ({ id, accountId, feeAmount = 0 }) => ({
      url: `${basePath}/occurrences/${id}/review`,
      method: 'post',
      body: { accountId, feeAmount },
    }),
    invalidatesTags: ['recurring-expense-occurrences', 'running-dashboard', 'payments'],
  }),

  updateReviewedRecurringExpenseOccurrence: builder.mutation<any, { id: number; accountId: number; feeAmount?: number }>({
    query: ({ id, accountId, feeAmount = 0 }) => ({
      url: `${basePath}/occurrences/${id}/review`,
      method: 'put',
      body: { accountId, feeAmount },
    }),
    invalidatesTags: ['recurring-expense-occurrences', 'running-dashboard', 'payments'],
  }),
})

export default recurringExpensesEndpoints
